import React, { useEffect, useRef, useState } from 'react';
import { Guardrail, GuardrailEnforcement } from '../types';

/**
 * Single-textarea guardrails editor. The author writes one rule per line; each
 * line is a plain-English rule the output must satisfy. A line may start with an
 * inline level marker to change how a violation is handled:
 *
 *   [block] Never include real secrets or API keys   ← fails/retries the step
 *   [warn]  Prefer concise copy                       ← allowed, but flagged
 *   [off]   Legacy rule, temporarily disabled         ← ignored
 *   Must cover auth and error states                  ← no marker = block (default)
 *
 * Under the hood this is still a `Guardrail[]` (so nothing else in the stack
 * changes) — we serialize it to text for editing and parse it back on change.
 */
export function GuardrailsEditor({
  guardrails,
  onChange,
  onSave,
  saving,
  canSave,
  saveLabel = 'Save',
}: {
  guardrails: Guardrail[];
  onChange: (next: Guardrail[]) => void;
  /** When provided, a Save button appears in the footer. */
  onSave?: () => void | Promise<void>;
  saving?: boolean;
  /** Whether Save is enabled (e.g. there are unsaved changes). Defaults to true. */
  canSave?: boolean;
  saveLabel?: string;
}) {
  // Local text state so typing is smooth (no re-serialize-on-every-keystroke
  // fighting the cursor). We re-sync only when the parent's guardrails change to
  // something that doesn't match what we last emitted (e.g. loading a different
  // assembly line, or a version switch).
  const [text, setText] = useState<string>(() => serialize(guardrails));
  const lastEmitted = useRef<string>(serialize(guardrails));

  useEffect(() => {
    const incoming = serialize(guardrails);
    if (incoming !== lastEmitted.current) {
      setText(incoming);
      lastEmitted.current = incoming;
    }
  }, [guardrails]);

  const handle = (value: string): void => {
    setText(value);
    const parsed = parseGuardrailText(value);
    lastEmitted.current = serialize(parsed);
    onChange(parsed);
  };

  const clearAll = (): void => {
    setText('');
    lastEmitted.current = '';
    onChange([]);
  };

  const count = guardrails.length;
  const warnCount = guardrails.filter((g) => g.enforcement === 'warn').length;
  const offCount = guardrails.filter((g) => g.enforcement === 'off').length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
      <textarea
        className="input"
        value={text}
        onChange={(e) => handle(e.target.value)}
        placeholder={
          'One rule per line, e.g.\n' +
          'Never include real secrets or API keys.\n' +
          '[warn] Prefer concise, scannable copy.'
        }
        rows={4}
        style={{ width: '100%', resize: 'vertical', minHeight: 84, lineHeight: 1.5 }}
        spellCheck={false}
      />
      <span className="t-micro muted">
        One rule per line. Prefix with{' '}
        <code>[warn]</code> (flag only) or <code>[off]</code> (ignore) to change the level — default is{' '}
        <code>[block]</code>, which fails the step on violation.
      </span>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2 }}>
        <span className="t-micro muted">
          {count === 0
            ? 'No guardrails'
            : `${count} rule${count !== 1 ? 's' : ''}${warnCount ? ` · ${warnCount} warn` : ''}${offCount ? ` · ${offCount} off` : ''}`}
        </span>
        <span style={{ flex: 1 }} />
        {count > 0 && (
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            style={{ color: 'var(--err)' }}
            onClick={clearAll}
            title="Remove all guardrails on this step"
          >
            Clear
          </button>
        )}
        {onSave && (
          <button
            type="button"
            className="btn btn--accent btn--sm"
            disabled={saving || canSave === false}
            onClick={() => void onSave()}
          >
            {saving ? 'Saving…' : saveLabel}
          </button>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  text  ⇄  Guardrail[]                                              */
/* ------------------------------------------------------------------ */

const MARKER_RE = /^\s*\[(block|warn|off)\]\s*(.*)$/i;

/** Serialize guardrails to one-rule-per-line text with `[warn]`/`[off]` markers. */
function serialize(guardrails: Guardrail[]): string {
  return (guardrails ?? [])
    .map((g) => {
      const text = (g.text ?? '').trim();
      if (!text) return '';
      return g.enforcement === 'block' ? text : `[${g.enforcement}] ${text}`;
    })
    .filter((line) => line.length > 0)
    .join('\n');
}

/**
 * Parse editor text into guardrails. Blank lines are skipped; a leading
 * `[block]`/`[warn]`/`[off]` marker sets the level (default `block`). Ids are
 * assigned by line index so they stay stable across edits.
 */
function parseGuardrailText(text: string): Guardrail[] {
  const out: Guardrail[] = [];
  const lines = text.split('\n');
  for (const raw of lines) {
    const line = raw.trim();
    if (!line) continue;
    const m = line.match(MARKER_RE);
    let enforcement: GuardrailEnforcement = 'block';
    let body = line;
    if (m) {
      enforcement = m[1].toLowerCase() as GuardrailEnforcement;
      body = m[2].trim();
    }
    if (!body) continue; // a bare "[warn]" with no rule text
    out.push({ id: `g${out.length + 1}`, text: body, enforcement });
  }
  return out;
}
